"use client";

import React, { useState, useRef, useEffect } from "react";
import { Globe, Check } from "lucide-react";
import { useLanguage } from "@/lib/i18n/context";
import { Language, SUPPORTED_LANGUAGES } from "@/lib/i18n/types";

export function LanguageSelector({
  className = "",
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}) {
  const { language, setLanguage, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const currentOption =
    SUPPORTED_LANGUAGES.find((l) => l.code === language) || SUPPORTED_LANGUAGES[0];

  const handleSelectLanguage = (code: Language) => {
    setLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title={t("layout.language.selectLanguage")}
        aria-label="Toggle language selector"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-surface-muted hover:bg-surface-cardHover text-themed-textPrimary border border-themed-border hover:border-themed-borderAccent text-xs font-semibold transition-all shadow-sm focus:outline-none"
      >
        <span className="text-sm leading-none">{currentOption.flag}</span>
        {!compact && (
          <span className="font-mono text-xs uppercase tracking-wider text-themed-textSecondary font-bold">
            {currentOption.code}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-surface-elevated border border-themed-border rounded-2xl shadow-2xl z-50 p-1.5 animate-in fade-in-50 zoom-in-95 duration-150 backdrop-blur-xl text-xs">
          <div className="px-2.5 py-1.5 border-b border-themed-borderSubtle flex items-center gap-2 mb-1">
            <Globe className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
            <span className="font-bold text-themed-textPrimary tracking-tight">
              {t("layout.language.title")}
            </span>
          </div>

          <div className="space-y-0.5">
            {SUPPORTED_LANGUAGES.map((langOption) => {
              const isSelected = langOption.code === language;
              return (
                <button
                  key={langOption.code}
                  type="button"
                  onClick={() => handleSelectLanguage(langOption.code)}
                  className={`w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-left transition-all ${
                    isSelected
                      ? "bg-[var(--accent-primary)]/10 text-themed-textPrimary border border-[var(--accent-primary)]/30 font-bold shadow-sm"
                      : "text-themed-textSecondary hover:bg-surface-cardHover hover:text-themed-textPrimary"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-base">{langOption.flag}</span>
                    <div className="flex flex-col">
                      <span className="text-xs">{langOption.nativeName}</span>
                      <span className="text-[10px] text-themed-textMuted font-mono">
                        {langOption.name}
                      </span>
                    </div>
                  </div>

                  {isSelected && (
                    <Check className="w-4 h-4 text-[var(--accent-primary)] shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
